/**
 * Activity dashboard API client (#579).
 *
 * Wraps GET /api/v1/activity/* endpoints. Tenant scoping is enforced
 * server-side from the JWT, so the client never passes tenant_id —
 * leaking that out of the FE would be the kind of mistake that lets a
 * malicious admin pivot into another tenant's data.
 *
 * Uses the shared apiClient from services/api.ts so it inherits the app's
 * 401 → refresh → retry → /login flow. A separate axios instance would
 * hard-fail expired-but-refreshable sessions on /admin/activity, which
 * is exactly the surprise we don't want for an admin-only page.
 */
import apiClient from './api';

export type ActivityPeriod = '1d' | '7d' | '30d' | '90d';
export type ActivityResult = 'success' | 'failure' | 'error';
export type AnomalySeverity = 'low' | 'medium' | 'high' | 'critical';

export interface CategoryCount {
  action_category: string;
  count: number;
}

export interface TopUser {
  user_id: string | null;
  event_count: number;
}

export interface AnomalySeverityBreakdown {
  low: number;
  medium: number;
  high: number;
  critical: number;
}

export interface AnomalySummary {
  total: number;
  unresolved: number;
  by_severity: AnomalySeverityBreakdown;
}

export interface ActivitySummary {
  period: ActivityPeriod;
  period_start: string;
  period_end: string;
  total_events: number;
  unique_users: number;
  by_category: CategoryCount[];
  top_users: TopUser[];
  anomalies: AnomalySummary;
}

export interface AuditEventRow {
  id: string;
  created_at: string;
  action: string;
  action_category: string;
  result: ActivityResult;
  tenant_id: string | null;
  user_id: string | null;
  session_id: string | null;
  resource_type: string | null;
  resource_id: string | null;
  resource_name: string | null;
  request_id: string | null;
  duration_ms: number | null;
  ip_address: string | null;
  error_message: string | null;
}

export interface AuditEventPage {
  items: AuditEventRow[];
  total: number;
  offset: number;
  limit: number;
}

export interface AnomalyRow {
  id: number;
  created_at: string;
  event_type: string;
  severity: AnomalySeverity;
  tenant_id: string | null;
  user_id: string | null;
  session_id: string | null;
  request_path: string | null;
  request_method: string | null;
  response_status: number | null;
  ip_address: string | null;
  detail: Record<string, unknown>;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
}

export interface AnomalyPage {
  items: AnomalyRow[];
  total: number;
}

export const activityService = {
  async getSummary(period: ActivityPeriod = '7d'): Promise<ActivitySummary> {
    const res = await apiClient.get<ActivitySummary>('/activity/summary', {
      params: { period },
    });
    return res.data;
  },

  async listEvents(params: {
    user_id?: string;
    action_category?: string;
    result?: ActivityResult;
    date_from?: string;
    date_to?: string;
    offset?: number;
    limit?: number;
  } = {}): Promise<AuditEventPage> {
    const res = await apiClient.get<AuditEventPage>('/activity/events', { params });
    return res.data;
  },

  /**
   * Triggers a CSV download in the browser. Returns the blob URL after
   * revoking is the caller's responsibility (or use within an <a> click flow).
   */
  async exportEventsCsv(params: {
    date_from?: string;
    date_to?: string;
    action_category?: string;
    result?: ActivityResult;
  } = {}): Promise<Blob> {
    const res = await apiClient.get('/activity/events/export', {
      params,
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  async listAnomalies(params: {
    severity?: AnomalySeverity;
    resolved?: boolean;
    offset?: number;
    limit?: number;
  } = {}): Promise<AnomalyPage> {
    const res = await apiClient.get<AnomalyPage>('/activity/anomalies', { params });
    return res.data;
  },

  async resolveAnomaly(id: number, note?: string): Promise<AnomalyRow> {
    const res = await apiClient.post<AnomalyRow>(
      `/activity/anomalies/${id}/resolve`,
      { note: note ?? null },
    );
    return res.data;
  },
};
